// Mobile store request form. Line-item description opens F7 popup autocomplete (creatable).
import React, { Fragment, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { Field, useForm } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { OnChange } from "react-final-form-listeners";
import { Block, BlockTitle, List, ListItem, Link, Stepper, f7, f7ready } from "framework7-react";
import _ from "lodash";

import { InputField, SmartSelect, ToggleField, required, isNotEmpty } from "modules/4_final_form/mobile";
import { getEmployees } from "modules/employees/actions";
import { getProjects } from "modules/projects/actions";
import { getMWProducts } from "modules/moneyworks/products/actions";

const QtyStepper = ({ input }) => (
  <Stepper small raised min={1} value={Number(input.value) || 1} onStepperChange={(v) => input.onChange(v)} />
);

// ponytail: F7 popup autocomplete so the keyboard doesn't cover matches; "Use …" row is the creatable path
const ProductAutocompleteField = ({ input, meta, productOptions, placeholder, trailing, onEmptyClose, ...rest }) => {
  const openerId = `ac-${String(input.name).replace(/[^a-zA-Z0-9]/g, "-")}`;
  const optionsRef = useRef(productOptions);
  const onChangeRef = useRef(input.onChange);
  const valueRef = useRef(input.value);
  const onEmptyCloseRef = useRef(onEmptyClose);
  const openOnMount = useRef(!input.value);
  const { touched, error } = meta;
  const empty = !input.value;

  optionsRef.current = productOptions;
  onChangeRef.current = input.onChange;
  valueRef.current = input.value;
  onEmptyCloseRef.current = onEmptyClose;

  useEffect(() => {
    let ac;
    let cancelled = false;

    f7ready(() => {
      if (cancelled) return;
      const el = document.getElementById(openerId);
      if (!el) return;

      ac = f7.autocomplete.create({
        openerEl: el,
        openIn: "popup",
        closeOnSelect: true,
        autoFocus: true,
        requestSourceOnOpen: true,
        valueProperty: "value",
        textProperty: "label",
        pageTitle: "Item",
        searchbarPlaceholder: "Type a new item or search",
        popupCloseLinkText: "Close",
        source(query, render) {
          const q = (query || "").trim();
          const opts = optionsRef.current || [];
          const matches = q
            ? _.take(_.filter(opts, (o) => _.includes(o.label.toLowerCase(), q.toLowerCase())), 20)
            : _.take(opts, 20);
          const exact = q && _.some(opts, (o) => o.value === q || o.label === q);
          if (q && !exact) {
            render([{ label: `Add “${q}” as new item`, value: q }, ...matches]);
            return;
          }
          render(matches);
        },
        on: {
          change(value) {
            const selected = value?.[0];
            const text = typeof selected === "object" ? selected.value : selected;
            valueRef.current = text || "";
            onChangeRef.current(text || "");
          },
          closed() {
            if (!valueRef.current) onEmptyCloseRef.current?.();
          },
        },
      });
      if (openOnMount.current) ac.open();
    });

    return () => {
      cancelled = true;
      ac?.destroy();
    };
  }, [openerId]);

  return (
    <ListItem {...rest} className={`store-request-line${empty ? " store-request-placeholder" : ""}`}>
      <span slot="title" id={openerId} className="store-request-product">
        {input.value || placeholder || "Tap to select item"}
      </span>
      {trailing && (
        <div slot="after" className="store-request-line-after">
          {trailing}
        </div>
      )}
      {touched && error && (
        <span slot="footer" className="text-color-red">
          {error}
        </span>
      )}
    </ListItem>
  );
};

const LineItemsFields = ({ productOptions }) => {
  const form = useForm();

  return (
    <FieldArray name="line_items_attributes" validate={isNotEmpty}>
      {({ fields, meta: { error, touched } }) => {
        const hasItems = _.some(fields.value, (row) => !row?._destroy);

        return (
        <Fragment>
          <BlockTitle className="margin-top">Items</BlockTitle>
          {!hasItems && (
            <Block inset className="text-color-orange no-margin-top">
              No items added. Tap Add item below to add one.
            </Block>
          )}
          {hasItems && (
          <List inset strong outline>
            {fields.map((field, index) => {
              if (fields.value[index]?._destroy) return null;
              const remove = () => {
                const row = fields.value[index];
                if (row?.id) {
                  fields.update(index, { ...row, _destroy: true });
                } else {
                  fields.remove(index);
                }
              };

              return (
                <Field
                  key={field}
                  component={ProductAutocompleteField}
                  name={`${field}.description`}
                  validate={required}
                  placeholder="Tap to select item"
                  productOptions={productOptions}
                  slot="list"
                  onEmptyClose={remove}
                  trailing={
                    <Fragment>
                      <Field name={`${field}.quantity`} component={QtyStepper} validate={required} />
                      <Link href={false} iconF7="xmark" iconSize={18} color="red" onClick={remove} />
                    </Fragment>
                  }
                />
              );
            })}
          </List>
          )}
          {fields.map((field, index) => {
            if (fields.value[index]?._destroy) return null;
            return (
              <OnChange key={field} name={`${field}.description`}>
                {(value) => {
                  const opt = _.find(productOptions, { value });
                  form.change(`${field}.stockcode`, opt?.stockcode || (value ? "MISC" : null));
                }}
              </OnChange>
            );
          })}
          {_.isString(error) && touched && hasItems && (
            <Block className="no-margin-top padding-horizontal">
              <div className="text-color-red">{error}</div>
            </Block>
          )}
        </Fragment>
        );
      }}
    </FieldArray>
  );
};

function mapStateToProps(state) {
  return {
    projects: state.projects,
    employees: state.employees,
    mw_products: state.mw_products,
  };
}

const StoreRequestForm = (props) => {
  const { projects, employees, mw_products, values, form } = props;
  const toolAccount = !!values?.tool_account;

  useEffect(() => {
    if (_.isEmpty(projects)) {
      props.getProjects(true);
    }
    if (_.isEmpty(employees)) {
      props.getEmployees();
    }
    if (_.isEmpty(mw_products)) {
      props.getMWProducts();
    }
  }, []);

  const projectOptions = useMemo(
    () =>
      _.map(_.sortBy(projects, "number"), (p) => ({
        label: p.display_name,
        value: p.id,
      })),
    [projects],
  );

  const employeeOptions = useMemo(
    () =>
      _.map(_.sortBy(employees, ["last_name", "first_name"]), (emp) => ({
        value: emp.id,
        label: emp.display_name,
      })),
    [employees],
  );

  const productOptions = useMemo(
    () =>
      _.map(_.sortBy(mw_products, "code"), (p) => ({
        label: `${p.code} - ${p.description}`,
        value: `${p.code} - ${p.description}`,
        stockcode: p.code,
      })),
    [mw_products],
  );

  return (
    <div className="store-request-form">
      <List inset strong outline>
        <Field component={ToggleField} name="tool_account" label="Tool Account" slot="list" />
        <OnChange name="tool_account">
          {(value) => {
            if (value) {
              form.change("project_id", null);
            } else {
              form.change("tool_account_employee_id", null);
            }
          }}
        </OnChange>

        {!toolAccount && (
          <Field
            name="project_id"
            component={SmartSelect}
            options={projectOptions}
            validate={required}
            virtualList
            label="Project"
            placeholder="Select Project"
            valueName="value"
            labelName="label"
            slot="list"
            searchbar
            searchbarPlaceholder="Search Project"
          />
        )}

        {toolAccount && (
          <Field
            name="tool_account_employee_id"
            component={SmartSelect}
            options={employeeOptions}
            validate={required}
            label="Employee"
            placeholder="Select Employee"
            valueName="value"
            labelName="label"
            slot="list"
            searchbar
            searchbarPlaceholder="Search Employee"
          />
        )}

        <Field
          component={InputField}
          name="notes"
          type="textarea"
          label="Notes"
          placeholder="Optional"
          parse={(v) => v}
          resizable
          slot="list"
        />
      </List>

      <LineItemsFields productOptions={productOptions} />
    </div>
  );
};

export default connect(mapStateToProps, { getEmployees, getProjects, getMWProducts })(StoreRequestForm);
